import type { AxiosError } from 'axios';
import axios from 'axios';
import type { History } from 'history';
import { jwtDecode } from 'jwt-decode';

import { Capacitor } from '@capacitor/core';
import type { DeviceInfo } from '@capacitor/device';
import { SecurePreferences } from '@capawesome-team/capacitor-secure-preferences';
import { ErrorCode as VaultErrorCode } from '@capawesome-team/capacitor-vault';
import { isPlatform } from '@ionic/react';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { environment } from '../../environment/environment';
import { BASE_ENDPOINT, isNative } from '../../helpers/utils.helper';
import { APIStatus } from '../../models/api';
import type { ErrorMessage } from '../../models/error';
import type { AsyncThunkConfig } from '../../models/slice';
import { CryptoService } from '../../services/crypto.service';
import type { RaygunUser } from '../../services/raygun.service';
import { RaygunErrorHandlerService } from '../../services/raygun.service';
import type { AppDispatch } from '../../store';
import { getLanguage, saveLanguagePreference } from '../Preferences/languageSlice';
import { showLoading } from '../tabs/tabsSlice';
import {
  DeviceSecurityType,
  getStoredAuthResult,
  getStoredLogin,
  logVaultError,
  removeStoredAuthResult,
  setStoredAuthResult,
  setStoredLogin,
  setVaultLockType,
  type VaultPluginError,
} from './vault';

const { logError, setRaygunUser } = RaygunErrorHandlerService();

export class AuthResult {
  token: string | null = null;
  use_location: boolean | null = null;
  use_text_entry: boolean | null = null;
  use_mood: boolean | null = null;
  use_badge: boolean | null = null;
  use_streak_component: boolean | null = null;
  use_streak_component_title: boolean | null = null;
  use_client_journal_selection: boolean | null = null;
  use_cravings_quick_checkin: boolean | null = null;
  use_scheduler: boolean | null = null;
  privacy: boolean | null = null;
  waiver: boolean | null = null;
  first_name: string | null = null;
  last_name: string | null = null;
  temporary: boolean | null = null;
  expired?: boolean | null = null;
  account_status?: 'archived' | null = null;
  use_apa_features: boolean | null = null;
  patient_consent_form_acknowledgement: boolean | null = null;
  use_qr_code: boolean | null = null;
}

export type Login = {
  email: string;
  password: string;
};

export type TokenValue = {
  organization_id: string;
  user_id: string;
  role: string;
  exp: number;
  region: string;
};

export const Org = {
  APA: '8ceebcc8-2c82-49c7-ba46-1851079d10a0',
  OSI: '2e129b2f-d501-474e-bed1-6ebce470d7f7',
  ONTRAK: 'b52211b2-1971-4adb-8dad-c4ec4d552f3a',
} as const;

type Org = (typeof Org)[keyof typeof Org];

type ErrorTextAlias =
  | 'generic.checkWiFiAndTryAgain'
  | 'generic.errorMessage'
  | 'loginPage.loginArchivedClient'
  | 'loginPage.loginErrorMessage';

type LoginSliceType = {
  loginApiStatus: APIStatus;
  errorTextAlias: ErrorTextAlias | null;
  authResult: AuthResult;
  currentUser: TokenValue | undefined;
  refreshTokenStatus: APIStatus;
  resetPasswordStatus: APIStatus;
  doNotSendPasswordResetEmail: boolean;
  showBiometricErrorAlert: boolean;
  returningUser: boolean;
  lockType: DeviceSecurityType;
  canUseBiometrics: boolean;
  deviceInfo: DeviceInfo | null;
  notificationUrl: string;
  loginInfo: Login | null;
};

const initialState: LoginSliceType = {
  loginApiStatus: APIStatus.IDLE,
  refreshTokenStatus: APIStatus.IDLE,
  resetPasswordStatus: APIStatus.IDLE,
  doNotSendPasswordResetEmail: false,
  returningUser: true,
  authResult: new AuthResult(),
  currentUser: undefined,
  showBiometricErrorAlert: false,
  errorTextAlias: null,
  lockType: DeviceSecurityType.None,
  canUseBiometrics: false,
  deviceInfo: null,
  notificationUrl: '',
  loginInfo: null,
};

export const getVaultAuthResult = createAsyncThunk<AuthResult, undefined, AsyncThunkConfig>(
  'login/getVaultAuthResult',
  async (_, thunkAPI) => {
    try {
      const value = await getStoredAuthResult();
      const authResult = value ? (JSON.parse(value) as AuthResult) : null;
      return authResult?.token ? authResult : new AuthResult();
    } catch (e) {
      logVaultError(e, 'getVaultAuthResult');
      switch ((e as VaultPluginError).code) {
        case VaultErrorCode.UnlockCanceled:
        case VaultErrorCode.AuthenticatorUnavailable:
          return new AuthResult();
        case VaultErrorCode.KeyInvalidated:
        case VaultErrorCode.VaultNotFound:
          thunkAPI.dispatch(showBiometricAlert(true));
          await thunkAPI.dispatch(updateVaultLockType(DeviceSecurityType.None));
          await thunkAPI.dispatch(removeVaultValue(false));
          return new AuthResult();
        default:
          return new AuthResult();
      }
    }
  },
);

export const getVaultLogin = createAsyncThunk<Login | null, undefined, AsyncThunkConfig>(
  'login/getVaultLogin',
  async (_, _thunkAPI) => {
    try {
      const value = await getStoredLogin();
      return value ? (JSON.parse(value) as Login) : null;
    } catch (e) {
      logVaultError(e, 'getVaultLogin');
      return null;
    }
  },
);

const saveBiometricsEmail = async (email: string) => {
  if (!Capacitor.isPluginAvailable('Preferences')) {
    return;
  }

  if (email) {
    const encryptedEmail = await CryptoService.encrypt(email, environment.cryptoKey, 'saveBiometricsEmail');
    await SecurePreferences.set({
      key: environment.biometricsEmailKey,
      value: encryptedEmail,
    });
  }
};

// oxlint-disable-next-line typescript/no-invalid-void-type
export const setVaultLogin = createAsyncThunk<void, undefined, AsyncThunkConfig>(
  'login/setVaultLogin',
  async (_, thunkAPI) => {
    const loginInfo = thunkAPI.getState().login.loginInfo;
    try {
      if (loginInfo) {
        await setStoredLogin(JSON.stringify(loginInfo));
        await saveBiometricsEmail(loginInfo.email);
      }
      thunkAPI.dispatch(setLoginInfo(null));
    } catch (e) {
      logVaultError(e, 'setVaultLogin');
    }
  },
);

// oxlint-disable-next-line @typescript-eslint/no-invalid-void-type
export const updateVaultLockType = createAsyncThunk<void, DeviceSecurityType, AsyncThunkConfig>(
  'login/updateVaultLockType',
  async (lockType, thunkAPI) => {
    try {
      await setVaultLockType(lockType);
      thunkAPI.dispatch(setLockType(lockType));
    } catch (e) {
      logVaultError(e, 'updateVaultLockType');
      throw e;
    }
  },
);

// oxlint-disable-next-line @typescript-eslint/no-invalid-void-type
export const removeVaultValue = createAsyncThunk<void, boolean, AsyncThunkConfig>(
  'login/removeVaultValue',
  async (isLogout, _thunkAPI) => {
    try {
      await removeStoredAuthResult(isLogout);
    } catch (e) {
      logVaultError(e, 'removeVaultValue');
    }
  },
);

const saveWebUser = async (user: AuthResult) => {
  if (!Capacitor.isPluginAvailable('Preferences')) {
    return;
  }

  if (user?.token) {
    const encryptedAuthResult = await CryptoService.encrypt(JSON.stringify(user), environment.cryptoKey, 'saveWebUser');
    await SecurePreferences.set({
      key: environment.authResultKey,
      value: encryptedAuthResult,
    });
  }
};

export const decodeToken = (user: AuthResult) => {
  try {
    if (user?.token) {
      return jwtDecode<TokenValue>(user.token);
    }
  } catch (error) {
    logError(error, ['loginSlice', 'decodeToken']);
  }
};

const setUserRaygun = (user: AuthResult) => {
  const decodedToken = decodeToken(user);
  if (decodedToken) {
    const raygunUser: RaygunUser = {
      role: decodedToken.role,
      user_id: decodedToken.user_id,
      organization_id: decodedToken.organization_id,
      region: decodedToken.region,
    };
    setRaygunUser(raygunUser);
  }
};

export const updateUser = async (user: AuthResult, dispatch: AppDispatch, storeAuth: boolean) => {
  if (storeAuth) {
    if (isNative()) {
      try {
        await setStoredAuthResult(JSON.stringify(user));
      } catch (e) {
        const error = e as VaultPluginError;
        logVaultError(error, 'updateUser');
        if (
          [VaultErrorCode.KeyInvalidated, VaultErrorCode.AuthenticatorUnavailable].includes(
            error.code as VaultErrorCode,
          )
        ) {
          // To handle error for Android when user disabled device biometrics
          await dispatch(updateVaultLockType(DeviceSecurityType.None));
          await setStoredAuthResult(JSON.stringify(user));
        }
      }
    } else {
      await saveWebUser(user);
    }
  }

  setUserRaygun(user);
  dispatch(updateAuth(user));
};

const handleSuccessfulLogin = async (
  authResult: AuthResult,
  notificationUrl: string,
  dispatch: AppDispatch,
  history: History,
  login: Login,
  biometrics: boolean,
) => {
  await updateUser(authResult, dispatch, !authResult.temporary);
  const lng = getLanguage();
  if (lng) {
    await dispatch(saveLanguagePreference(lng));
  }

  if (isNative()) {
    if (Capacitor.isPluginAvailable('Device')) {
      const { Device } = await import('@capacitor/device');
      const info = await Device.getInfo();
      dispatch(setDeviceInfo(info));
    }
    if (!biometrics) {
      dispatch(setLoginInfo(login));
    }
  }

  if (authResult.temporary) {
    dispatch(showLoading(false));
    history.push('/reset-password');
  } else {
    if (isNative() && isPlatform('ios') && !biometrics) {
      const { SavePassword } = await import('capacitor-ios-autofill-save-password');
      SavePassword.promptDialog({
        username: login.email,
        password: login.password,
      }).catch((error) => {
        logError(error, ['loginSlice', 'handleSuccessfulLogin', 'SavePassword', 'promptDialog']);
      });
    }
    if (!authResult.waiver) {
      dispatch(showLoading(false));
      history.push('/accepted_waiver');
    } else if (!authResult.privacy) {
      dispatch(showLoading(false));
      history.push('/privacy_policy');
    } else {
      if (notificationUrl) {
        history.push(notificationUrl);
        dispatch(setNotificationUrl(''));
      } else {
        history.push('/tabs/home');
      }
    }
  }
};

export const doLogin = createAsyncThunk<boolean, Login & { history: History; biometrics: boolean }, AsyncThunkConfig>(
  'login/doLogin',
  async ({ history, biometrics, ...login }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoading(true)); // loadHome will remove it

      const endpoint = 'v2_login';
      const body = {
        ...login,
        biometrics,
        isNative: isNative(),
      };
      const [resp1, resp2] = await Promise.allSettled([
        axios.post(endpoint, body, { baseURL: BASE_ENDPOINT.CA }) as unknown as [AuthResult],
        axios.post(endpoint, body, { baseURL: BASE_ENDPOINT.US }) as unknown as [AuthResult],
      ]);

      const notificationUrl = thunkAPI.getState().login.notificationUrl;
      if (resp1.status === 'fulfilled' && resp1.value[0].token) {
        await handleSuccessfulLogin(resp1.value[0], notificationUrl, thunkAPI.dispatch, history, login, biometrics);
        return true;
      } else if (resp2.status === 'fulfilled' && resp2.value[0].token) {
        await handleSuccessfulLogin(resp2.value[0], notificationUrl, thunkAPI.dispatch, history, login, biometrics);
        return true;
      } else if (
        (resp1.status === 'fulfilled' && resp1.value[0].expired) ||
        (resp2.status === 'fulfilled' && resp2.value[0].expired)
      ) {
        thunkAPI.dispatch(showLoading(false));
        thunkAPI.dispatch(setApiStatusAndTextError({ loginApiStatus: APIStatus.EXPIRED }));
      } else if (
        (resp1.status === 'fulfilled' && resp1.value[0].account_status === 'archived') ||
        (resp2.status === 'fulfilled' && resp2.value[0].account_status === 'archived')
      ) {
        thunkAPI.dispatch(showLoading(false));
        thunkAPI.dispatch(
          setApiStatusAndTextError({
            loginApiStatus: APIStatus.FAILED,
            errorTextAlias: 'loginPage.loginArchivedClient',
          }),
        );
      } else if (resp1.status === 'rejected') {
        throw resp1.reason;
      } else if (resp2.status === 'rejected') {
        throw resp2.reason;
      } else {
        thunkAPI.dispatch(showLoading(false));
        thunkAPI.dispatch(
          setApiStatusAndTextError({
            loginApiStatus: APIStatus.FAILED,
            errorTextAlias: 'loginPage.loginErrorMessage',
          }),
        );
      }
      return false;
    } catch (e) {
      logError(e, ['loginSlice', 'doLogin']);
      thunkAPI.dispatch(showLoading(false));
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

const checkAcceptPolicy = (authResult: AuthResult, history: History) => {
  if (!authResult.waiver) {
    history.push('/accepted_waiver');
  } else if (!authResult.privacy) {
    history.push('/privacy_policy');
  } else if (history && (history.location.pathname === '/reset-password' || history.location.pathname === '/login')) {
    history.push('/tabs/home');
  }
};

export const refreshToken = createAsyncThunk<
  [AuthResult],
  { history: History | null; isJournaling: boolean; storeAuth?: boolean },
  AsyncThunkConfig
>('login/refreshToken', async ({ history, isJournaling, storeAuth }, thunkAPI) => {
  try {
    const response = (await axios.post('v2_token_refresh', {
      isJournaling,
      isNative: isNative(),
    })) as [AuthResult];

    const [authResult] = response;
    if (authResult?.token) {
      await updateUser(authResult, thunkAPI.dispatch, storeAuth ?? false);
      if (history && history.location && history.location.pathname !== '/privacy_policy') {
        checkAcceptPolicy(authResult, history);
      }
    }
    return response ?? [new AuthResult()];
  } catch (e) {
    logError(e, ['loginSlice', 'refreshToken']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const verifyToken = createAsyncThunk<boolean, undefined, AsyncThunkConfig>(
  'login/verifyToken',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v2_token_verify', {})) as boolean;
      return response ?? false;
    } catch (e) {
      logError(e, ['loginSlice', 'verifyToken']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const resetPassword = createAsyncThunk<
  [AuthResult],
  { history: History | null; password: string },
  AsyncThunkConfig
>('login/resetPassword', async ({ history, password }, thunkAPI) => {
  try {
    const response = (await axios.post('v2_password_reset', { password, isNative: isNative() })) as [AuthResult];

    const [authResult] = response;
    if (authResult?.token) {
      const loginInfo = thunkAPI.getState().login.loginInfo;
      if (loginInfo) {
        thunkAPI.dispatch(setLoginInfo({ ...loginInfo, password }));
      }
      await updateUser(authResult, thunkAPI.dispatch, true);
      if (history && history.location && history.location.pathname !== '/privacy_policy') {
        checkAcceptPolicy(authResult, history);
      }
    }
    return response ?? [new AuthResult()];
  } catch (e) {
    logError(e, ['loginSlice', 'resetPassword']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    resetApiStatusAndErrorText: (state) => {
      state.errorTextAlias = null;
      state.loginApiStatus = APIStatus.IDLE;
    },
    setApiStatusAndTextError: (
      state,
      action: PayloadAction<{ loginApiStatus: APIStatus; errorTextAlias?: ErrorTextAlias }>,
    ) => {
      state.errorTextAlias = action.payload.errorTextAlias || null;
      state.loginApiStatus = action.payload.loginApiStatus;
    },
    updateAuth: (state, action: PayloadAction<AuthResult>) => {
      state.authResult = action.payload;
      state.currentUser = decodeToken(action.payload);
    },
    setLockType: (state, action: PayloadAction<DeviceSecurityType | undefined>) => {
      state.lockType = action.payload || DeviceSecurityType.None;
    },
    showBiometricAlert: (state, action: PayloadAction<boolean>) => {
      state.showBiometricErrorAlert = action.payload;
    },
    notSendResetPasswordEmail: (state) => {
      state.doNotSendPasswordResetEmail = !state.doNotSendPasswordResetEmail;
    },
    onBoardingClickNewUSer: (state) => {
      state.returningUser = false;
    },
    onBoardingClickReturningUser: (state) => {
      state.returningUser = true;
    },
    setCanUseBiometrics: (state, action: PayloadAction<boolean>) => {
      state.canUseBiometrics = action.payload;
    },
    setDeviceInfo: (state, action: PayloadAction<DeviceInfo>) => {
      state.deviceInfo = action.payload;
    },
    setNotificationUrl: (state, action: PayloadAction<string>) => {
      state.notificationUrl = action.payload;
    },
    setLoginInfo: (state, action: PayloadAction<Login | null>) => {
      state.loginInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(doLogin.pending, (state, _action) => {
        state.loginApiStatus = APIStatus.PENDING;
      })
      .addCase(doLogin.fulfilled, (state, action) => {
        if (action.payload) {
          state.loginApiStatus = APIStatus.FULFILLED;
        }
      })
      .addCase(doLogin.rejected, (state, action) => {
        if ((action.payload as ErrorMessage)?.displayText === 'Network Error') {
          state.errorTextAlias = 'generic.checkWiFiAndTryAgain';
        } else {
          state.errorTextAlias = 'generic.errorMessage';
        }
        state.loginApiStatus = APIStatus.ERROR;
      })
      .addCase(refreshToken.pending, (state, _action) => {
        state.refreshTokenStatus = APIStatus.PENDING;
      })
      .addCase(refreshToken.fulfilled, (state, _action) => {
        state.refreshTokenStatus = APIStatus.FULFILLED;
      })
      .addCase(refreshToken.rejected, (state, _action) => {
        state.errorTextAlias = 'generic.errorMessage';
        state.refreshTokenStatus = APIStatus.ERROR;
      })
      .addCase(resetPassword.pending, (state, _action) => {
        state.resetPasswordStatus = APIStatus.PENDING;
      })
      .addCase(resetPassword.fulfilled, (state, _action) => {
        state.resetPasswordStatus = APIStatus.FULFILLED;
      })
      .addCase(resetPassword.rejected, (state, _action) => {
        state.resetPasswordStatus = APIStatus.ERROR;
      });
  },
});

export const {
  resetApiStatusAndErrorText,
  setApiStatusAndTextError,
  updateAuth,
  notSendResetPasswordEmail,
  onBoardingClickNewUSer,
  onBoardingClickReturningUser,
  setLockType,
  showBiometricAlert,
  setDeviceInfo,
  setCanUseBiometrics,
  setNotificationUrl,
  setLoginInfo,
} = loginSlice.actions;
